const RELOAD_MARKER_KEY = 'entite:stale-chunk-reload'

/** Keep a failed reload from immediately reloading the same broken asset forever. */
export const STALE_CHUNK_RELOAD_GUARD_MS = 10_000

const STALE_CHUNK_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /chunkloaderror/i,
  /loading (?:css )?chunk [\w-]+ failed/i,
  /unable to preload css/i,
]

interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

interface ReloadLocation {
  pathname: string
  search: string
  reload(): void
}

interface ReloadMarker {
  target: string
  at: number
}

interface RecoveryEnvironment {
  location: ReloadLocation
  storage: StorageLike
  now?: () => number
}

const errorText = (error: unknown): string => {
  if (error instanceof Error) return `${error.name}: ${error.message}`
  return typeof error === 'string' ? error : ''
}

export const isStaleChunkError = (error: unknown): boolean => {
  const text = errorText(error)
  return (
    text.length > 0 &&
    STALE_CHUNK_PATTERNS.some((pattern) => pattern.test(text))
  )
}

const readMarker = (storage: StorageLike): ReloadMarker | null => {
  try {
    const raw = storage.getItem(RELOAD_MARKER_KEY)
    if (raw === null) return null
    const value = JSON.parse(raw) as Partial<ReloadMarker>
    return typeof value.target === 'string' && typeof value.at === 'number'
      ? { target: value.target, at: value.at }
      : null
  } catch {
    return null
  }
}

/**
 * Reload once when a deployment removed a lazy-route asset referenced by the old app shell.
 * Returns `false` when this is not a chunk error, storage is unavailable, or this URL already
 * reloaded inside the guard window. The last two cases deliberately show the normal error UI
 * instead of risking a reload loop.
 */
export const attemptStaleChunkRecovery = (
  error: unknown,
  environment?: RecoveryEnvironment,
): boolean => {
  if (!isStaleChunkError(error)) return false

  const location = environment?.location ?? window.location
  const storage = environment?.storage ?? window.sessionStorage
  const now = environment?.now?.() ?? Date.now()
  const target = `${location.pathname}${location.search}`
  const marker = readMarker(storage)

  if (
    marker?.target === target &&
    now - marker.at < STALE_CHUNK_RELOAD_GUARD_MS
  )
    return false

  try {
    storage.setItem(
      RELOAD_MARKER_KEY,
      JSON.stringify({ target, at: now } satisfies ReloadMarker),
    )
  } catch {
    return false
  }

  location.reload()
  return true
}

export const clearStaleChunkRecoveryMarker = (storage?: StorageLike): void => {
  try {
    ;(storage ?? window.sessionStorage).removeItem(RELOAD_MARKER_KEY)
  } catch {
    // Storage can be blocked by browser policy. There is no marker to clean in that case.
  }
}

export const reloadCurrentPage = (): void => window.location.reload()
